import { RenderProofCacheState } from './api/RenderProofResponse';

const BYTES_PER_KB = 1024;

export const NO_VALUE = '—';

/** Shown when a metric did not move; design renders it in a neutral tag. */
export const NO_CHANGE = 'No change';

/**
 * Ratios below this read as a percentage instead of a multiple. Rounding to
 * whole multiples is only honest from 2x up: below it `Math.round` either
 * flattens the movement to a meaningless "1x" or — at 1.5x — rounds *up* and
 * overstates a half-again gain as a doubling.
 */
const MEANINGFUL_RATIO = 2;

/** Percentage movements smaller than this are noise, not an improvement. */
const MEANINGFUL_PERCENT = 5;

export function formatByteSize(bytes: number): string {
  const kilobytes = Math.round(bytes / BYTES_PER_KB);
  // A page with content should never read as `≈0 KB`; rounding alone does that
  // to anything under half a kilobyte.
  if (kilobytes === 0 && bytes > 0) return '<1 KB';
  return `≈${kilobytes} KB`;
}

export type ImprovementKind = 'multiplier' | 'delta';

export function formatImprovement(
  before: number,
  after: number,
  kind: ImprovementKind,
): string {
  if (kind === 'multiplier') {
    // Either side being empty leaves nothing to compare — a ratio would come out
    // as Infinity or NaN.
    if (before === 0 || after === 0) return NO_VALUE;
    if (before === after) return NO_CHANGE;

    // Always the larger over the smaller, so the figure stays readable: a
    // rendered page a fraction of the original used to round down to "0x more".
    // The direction goes in the wording, never in a shrinking multiplier.
    const ratio = after > before ? after / before : before / after;

    // Below this, rounding to whole multiples printed "1x more" — a figure that
    // says nothing while sitting in a tag that claims an improvement. A
    // percentage carries the actual movement; a ratio this small is noise.
    // Worded like the multiplier ("49% more") so the column reads consistently.
    if (ratio < MEANINGFUL_RATIO) {
      const percent = Math.round((after / before - 1) * 100);
      if (Math.abs(percent) < MEANINGFUL_PERCENT) return NO_CHANGE;
      // Worded, not signed: "31% less" reads as the improvement it is, where a
      // green tag containing "-31%" reads as a loss.
      return `${Math.abs(percent)}% ${percent > 0 ? 'more' : 'less'}`;
    }

    return after > before
      ? `${Math.round(ratio)}x more`
      : `${Math.round(ratio)}x less`;
  }

  const delta = after - before;
  // Design labels a metric that did not move "No change" rather than "+0",
  // for counts as well as for the size ratio.
  if (delta === 0) return NO_CHANGE;

  return `${delta > 0 ? '+' : ''}${delta}`;
}

/**
 * Which way a metric has to move to count as an improvement.
 *
 * Counts only improve by going up — fewer links is less for a crawler to follow.
 * Page weight improves either way: a bigger rendered page means content the
 * crawler could not see before, and a smaller one means the same content without
 * the script payload. Both are worth showing.
 */
export type ImprovementDirection = 'increase' | 'either';

export type Improvement = {
  label: string;
  /** True only for a movement worth showing as a gain — never for a sentinel. */
  isGain: boolean;
};

/**
 * The label plus whether it represents a real gain.
 *
 * Callers must not re-derive that from the label: `NO_VALUE` and `NO_CHANGE`
 * both mean "no gain", and a caller comparing against one of them alone
 * silently promotes the other to a green tag. Classifying here keeps the two in
 * step and defaults anything new to *not* a gain.
 */
export function describeImprovement(
  before: number,
  after: number,
  kind: ImprovementKind,
  direction: ImprovementDirection = 'increase',
): Improvement {
  const label = formatImprovement(before, after, kind);
  const moved = label !== NO_CHANGE && label !== NO_VALUE;
  const isGain = direction === 'either' ? moved : moved && after > before;

  return { label, isGain };
}

const STATUS_TEXT: Record<number, string> = {
  200: 'OK',
  301: 'Moved Permanently',
  302: 'Found',
  304: 'Not Modified',
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  410: 'Gone',
  429: 'Too Many Requests',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout',
  // Cloudflare's own code, not RFC-named — the one exotic status that shows up
  // in cached renders often enough to deserve a label. Other unmapped codes
  // stay a bare number on purpose.
  530: 'Unexpected Status',
};

export type RenderStatusLabel = {
  value: string;
  hint: string;
};

/**
 * Anything the crawler would not have cached reads the same way to the
 * customer: the integration works, this page needs a look. Distinguishing a
 * redirect from a 500 here only competes with the status code sitting next to
 * it, which already says which it was.
 */
function statusHint(statusCode: number): string {
  return isSuccessfulRenderStatus(statusCode)
    ? 'Render successful'
    : 'Needs attention';
}

/**
 * A render that did not return 2xx is not something to present as ready for
 * crawlers. An absent status counts as successful: older cached entries carry no
 * status in their page buffer.
 */
export function isSuccessfulRenderStatus(statusCode: number | undefined): boolean {
  if (statusCode === undefined) return true;
  return statusCode >= 200 && statusCode <= 299;
}

export function renderStatusLabel(
  statusCode: number | undefined,
): RenderStatusLabel {
  if (statusCode === undefined) {
    return { value: 'Unknown', hint: 'No render recorded yet' };
  }

  const text = STATUS_TEXT[statusCode];

  return {
    value: text === undefined ? `${statusCode}` : `${statusCode} ${text}`,
    hint: statusHint(statusCode),
  };
}

const CACHE_STATE_LABELS: Record<RenderProofCacheState, string> = {
  cached: 'Cached',
  caching: 'Caching',
  recaching: 'Recaching',
  not_cached: 'Not cached',
};

const CACHE_STATE_HINTS: Record<RenderProofCacheState, string> = {
  cached: 'Ready for crawler access',
  caching: 'Being prepared for crawlers',
  recaching: 'Being refreshed for crawlers',
  not_cached: 'Not available to crawlers yet',
};

export function cacheStateLabel(
  cacheState: RenderProofCacheState | undefined,
): string {
  return cacheState === undefined ? 'Unknown' : CACHE_STATE_LABELS[cacheState];
}

export function cacheStateHint(
  cacheState: RenderProofCacheState | undefined,
): string {
  return cacheState === undefined
    ? 'No render recorded yet'
    : CACHE_STATE_HINTS[cacheState];
}
